import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ScriptStatus } from '../../../shared/types';
import { ApiError } from '../../lib/api';
import { changeSentence, dayWhen, longWhen, publishApi, type PublishInfo, type PublishPreview, type PublishResponse } from '../../lib/publish';
import { useMediaQuery } from '../../lib/useMediaQuery';
import Button from '../Button';
import Eyebrow from '../Eyebrow';
import MobileSheet from '../MobileSheet';
import PublishOption from './PublishOption';
import SummaryBullets, { type SummaryState } from './SummaryBullets';

/** A named approver's sign-off (P-b). Supplied by package 3A; absent today. */
export interface SignOff {
  name: string;
  signedAt: number | null;
}

export interface PublishOutcome {
  result: PublishResponse;
  quiet: boolean;
  emailed: boolean;
  withoutSummary: boolean;
}

interface Props {
  scriptId: string;
  title: string;
  clientName: string;
  status: ScriptStatus;
  info: PublishInfo;
  /** The doc_version Henry is looking at, read after the flush. */
  expectedVersion: () => number;
  /** Lands any pending autosave; false when it could not. */
  flush: () => Promise<boolean>;
  /** The status pill asked for a later round. */
  preferRound?: boolean;
  targetRound?: number;
  /** P-b, wired in 3A: partial sign-offs that a new round resets. */
  signOffs?: SignOff[];
  onClose: () => void;
  onPublished: (outcome: PublishOutcome) => void;
  onOpenAdminSettings?: () => void;
  onReviewSuggestion?: () => void;
}

type Mode = 'round' | 'quiet';

/**
 * The Publish dialog (D1, mock-up 05 §02): one question, two answers. New
 * round moves v2 to v3 and can email the client what changed; Quiet update
 * fixes the current round with no email. After approval only Quiet update is
 * offered (P-a) and its note is required (D9). Enter never submits: one
 * wrong key should never email a client.
 */
export default function PublishDialog(props: Props) {
  const { scriptId, title, clientName, status, info, expectedVersion, flush, preferRound, targetRound, signOffs, onClose, onPublished } = props;
  const desktop = useMediaQuery('(min-width: 768px)');
  const [preview, setPreview] = useState<PublishPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('quiet');
  const [email, setEmail] = useState(false);
  const [note, setNote] = useState('');
  const [bullets, setBullets] = useState<string[]>([]);
  const [summary, setSummary] = useState<SummaryState>('loading');
  const [aiWritten, setAiWritten] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const summaryAsked = useRef(0);

  const approved = status === 'approved';

  const askSummary = useCallback(async () => {
    const ticket = ++summaryAsked.current;
    setSummary('loading');
    try {
      const res = await publishApi.summary(scriptId);
      if (ticket !== summaryAsked.current) return;
      if (res.bullets.length > 0) {
        setBullets(res.bullets);
        setAiWritten(true);
        setSummary('ready');
      } else {
        setSummary('failed');
      }
    } catch {
      if (ticket === summaryAsked.current) setSummary('failed');
    }
  }, [scriptId]);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const p = await publishApi.preview(scriptId);
      setPreview(p);
      const roundAllowed = !approved && p.unpublished !== 'internal';
      const wantRound = p.first_publish || (roundAllowed && (targetRound !== undefined ? targetRound !== p.current.round : preferRound || p.feedback !== null));
      setMode(wantRound ? 'round' : 'quiet');
      setEmail(p.email.default_checked);
      if (!p.first_publish && roundAllowed) void askSummary();
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : "Couldn't read your changes. Try again.");
    }
  }, [scriptId, approved, preferRound, targetRound, askSummary]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const current = preview?.current.round ?? info.published_round ?? 1;
  const next = targetRound ?? preview?.next_round ?? current + 1;
  const first = preview?.first_publish ?? false;
  const internalOnly = preview?.unpublished === 'internal';
  const emailAvailable = preview?.email.available ?? false;
  const willEmail = mode === 'round' && email && emailAvailable;
  const resetting = mode === 'round' ? (signOffs ?? []).filter((s) => s.signedAt !== null) : [];
  const noteMissing = approved && note.trim().length === 0;

  async function publish() {
    if (busy || !preview) return;
    setBusy(true);
    setError(null);
    try {
      if (!(await flush())) {
        setError('Retry the save first, so the client gets what you see.');
        return;
      }
      const withoutSummary = mode === 'round' && !first && summary === 'loading';
      const lines = mode === 'round' && !withoutSummary ? bullets.map((b) => b.trim()).filter(Boolean) : [];
      const result = await publishApi.publish(scriptId, {
        expected_version: expectedVersion(),
        mode,
        target_round: targetRound,
        email: willEmail,
        bullets: lines,
        note: mode === 'quiet' ? note.trim() : undefined,
      });
      onPublished({ result, quiet: mode === 'quiet', emailed: result.email === 'queued', withoutSummary: withoutSummary && willEmail });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && (err.body as { error?: string } | undefined)?.error === 'stale') {
        await flush();
        await load();
        setError("Your latest edit hadn't saved yet. Saved now, check and publish again.");
      } else {
        setError(err instanceof ApiError ? err.message : "That didn't publish. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  const primaryLabel = first
    ? willEmail ? `Send v${next} and email` : `Send v${next}`
    : mode === 'quiet'
    ? 'Publish quiet update'
    : resetting.length > 0
    ? `Reset sign-offs and publish v${next}`
    : willEmail ? `Publish v${next} and email` : `Publish v${next}`;

  const reader = clientName ? `${clientName} is` : 'The client is';
  const feedbackLine = preview?.feedback
    ? `${preview.feedback.by || 'The client'} sent feedback on ${dayWhen(preview.feedback.at)}.`
    : `No feedback on v${current} yet.`;

  let heading: string;
  let lead: ReactNode;
  if (first) {
    heading = `Send v1 to ${clientName || 'the client'}`;
    lead = 'Nobody outside the studio has seen this script yet. Publishing makes the client link work.';
  } else if (status === 'draft') {
    heading = 'Publish your changes';
    lead = 'The page is down. Publishing puts it back.';
  } else if (approved) {
    heading = 'Publish a fix to the approved script';
    const when = info.approved_at ? ` on ${dayWhen(info.approved_at)}` : '';
    lead = `${info.approved_by_name ? `Approved by ${info.approved_by_name}${when}` : `Approved${when}`}. The approval and its certificate stay as they are.`;
  } else {
    heading = 'Publish your changes';
    lead = preview ? (
      <>
        {reader} reading <b className="text-fg">v{current}</b>
        {preview.current.published_at ? `, published ${longWhen(preview.current.published_at)}` : ''}. Since then:{' '}
        <b className="text-fg">{changeSentence(preview.changes)}</b> {feedbackLine}
      </>
    ) : (
      'Reading your changes…'
    );
  }

  const emailCheck = (label: string, detail: ReactNode) => (
    <label className={`flex items-start gap-2.5 text-[13.5px] ${emailAvailable ? 'text-fg cursor-pointer' : 'text-faint cursor-not-allowed'}`}>
      <input
        type="checkbox"
        checked={willEmail}
        disabled={!emailAvailable}
        onChange={(e) => setEmail(e.target.checked)}
        className="mt-[3px] w-4 h-4 accent-[var(--color-accent)] shrink-0"
      />
      <span>
        {label}
        <small className="block text-[12px] text-faint mt-0.5 leading-[1.45]">{detail}</small>
      </span>
    </label>
  );

  const emailDetail = (subject: string): ReactNode => {
    if (!preview) return null;
    if (preview.email.reason === 'no_client_email') {
      return (
        <>
          No client email on this script.{' '}
          {props.onOpenAdminSettings ? (
            <button type="button" className="font-semibold text-accent hover:underline" onClick={props.onOpenAdminSettings}>
              Add one in Admin settings
            </button>
          ) : (
            'Add one in Admin settings'
          )}
          , or send the link yourself.
        </>
      );
    }
    if (preview.email.reason === 'email_off') return 'Client emails are off until email sending is set up for this tool.';
    return `To ${preview.email.to} · “${subject}”`;
  };

  const noteField = (label: string) => (
    <label className="block">
      <span className="block text-[12.5px] font-medium text-muted mb-1.5">{label}</span>
      <input
        type="text"
        value={note}
        maxLength={300}
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
        className="w-full rounded-[8px] border border-line bg-card px-3 py-2 text-[13.5px] text-fg focus:outline-none focus:border-accent focus-visible:shadow-focus"
      />
    </label>
  );

  const body = (
    <div>
      <Eyebrow accent>// Publish</Eyebrow>
      <h2 className="font-display font-bold text-[24px] leading-[1.15] tracking-[-0.01em] mt-2 mb-2">{heading}</h2>
      <p className="text-[13.5px] text-muted leading-[1.55]">{lead}</p>

      {loadError && (
        <p className="mt-4 text-[13px] text-error">
          {loadError}{' '}
          <button type="button" className="font-semibold text-accent hover:underline" onClick={() => void load()}>Try again</button>
        </p>
      )}

      {preview && first && (
        <PublishOption name="publish-mode" value="round" selected title="Send v1" chip="Draft to v1" chipGo
          gloss="The client can review, comment and suggest edits." onSelect={() => setMode('round')}>
          {emailCheck("Email the client that it's ready", preview.email.available
            ? `To ${preview.email.to} · “Your script is ready to review”, with the approve-by date if set.`
            : emailDetail('Your script is ready to review'))}
        </PublishOption>
      )}

      {preview && !first && (
        <>
          <PublishOption
            name="publish-mode"
            value="round"
            selected={mode === 'round'}
            disabled={approved || internalOnly}
            title="New round"
            chip={approved ? 'Not available' : `v${current} to v${next}`}
            chipGo
            gloss={approved
              ? `Approved scripts don't start new rounds. To reopen for review, set the status back to Round ${Math.min(current, 3)} first.`
              : internalOnly
              ? "Only internal fields changed. The client won't see a difference."
              : `Send v${next} for review and tell the client what changed.`}
            onSelect={() => setMode('round')}
          >
            {emailCheck('Email the client', emailDetail(`Version ${next} of ${title} is ready`))}
            <SummaryBullets
              state={summary}
              bullets={bullets}
              round={next}
              aiWritten={aiWritten}
              emailing={willEmail}
              onChange={(b) => { setBullets(b); if (summary === 'failed') setSummary('ready'); }}
              onRewrite={() => void askSummary()}
            />
            {preview.pending_suggestion && (
              <p className="mt-3 text-[12.5px] text-muted">
                1 suggestion from {preview.pending_suggestion.author_name || 'the client'} is waiting.{' '}
                {props.onReviewSuggestion ? (
                  <button type="button" className="font-semibold text-accent hover:underline" onClick={props.onReviewSuggestion}>Review it first?</button>
                ) : 'Review it first?'}
              </p>
            )}
            {resetting.length > 0 && (
              <div className="mt-3 rounded-[8px] border border-warning/40 bg-warning/10 px-3 py-2.5 text-[12.5px] text-fg">
                <p className="font-semibold">{resetting.length} of {signOffs!.length} sign-offs will reset</p>
                <ul className="mt-1 text-muted">
                  {signOffs!.map((s) => (
                    <li key={s.name}>{s.signedAt ? `${s.name} signed v${current}, ${dayWhen(s.signedAt)}` : `${s.name}, not signed yet`}</li>
                  ))}
                </ul>
                <p className="mt-1 text-muted">All {signOffs!.length} will be asked to sign v{next}. A quiet update keeps the signatures.</p>
              </div>
            )}
          </PublishOption>

          <PublishOption
            name="publish-mode"
            value="quiet"
            selected={mode === 'quiet'}
            title="Quiet update"
            chip={approved ? 'Stays approved' : resetting.length > 0 || (signOffs ?? []).some((s) => s.signedAt) ? `Stays v${current} · keeps sign-offs` : `Stays v${current}`}
            gloss={approved
              ? 'For small fixes the client has already agreed to, like a typo or a name spelling. No email.'
              : `Fix something in v${current}. No email, no new round. The client sees the new text next time the page loads.`}
            onSelect={() => setMode('quiet')}
          >
            {noteField(approved ? 'What did you fix? (required after approval)' : 'What did you fix? (optional, kept in version history)')}
            {!approved && (
              <p className="mt-2 text-[12px] text-faint">Anyone with the page open sees “This script was updated”, as today.</p>
            )}
          </PublishOption>
        </>
      )}

      {error && <p role="alert" className="mt-4 text-[13px] text-error">{error}</p>}

      <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <span className="text-[12px] text-faint">Your working copy is saved either way.</span>
        <div className="flex flex-col-reverse md:flex-row gap-2 md:gap-3">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy} className="w-full md:w-auto">Cancel</Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => void publish()}
            disabled={busy || !preview || (mode === 'quiet' && noteMissing)}
            className="w-full md:w-auto whitespace-nowrap"
          >
            {busy ? 'Publishing…' : primaryLabel}
          </Button>
        </div>
      </div>
    </div>
  );

  if (!desktop) {
    return <MobileSheet open title={first ? 'Send v1' : 'Publish'} onClose={busy ? () => {} : onClose}>{body}</MobileSheet>;
  }

  return (
    <div
      className="fixed inset-0 bg-ink-900/70 backdrop-blur-md flex items-start justify-center px-6 pt-12 pb-6 z-50 animate-fade-in overflow-y-auto"
      onClick={busy ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={heading}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[560px] bg-card border border-line rounded-md p-7 shadow-flare-lg/30 animate-scale-in"
      >
        {body}
      </div>
    </div>
  );
}
